import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FIELD_ACTIONS, type FieldAction, type FieldComparison, type FieldValue } from "../../../api/content/curationApi";
import { classNames } from "../../../ui/styles/classNames";
import { iriLocalName } from "../../../shared/references/shortReferences";
import { predicateKey } from "./candidateModel";
import { changeToneClass } from "../../../ui/styles/changeStyles";
import FieldGroup from "../../../ui/forms/FieldGroup";
import OptionLabel from "../../../ui/forms/OptionLabel";
import AppButton from "../../../ui/buttons/AppButton";

/**
 * The new term and its possible duplicate side by side, field by field (CU-6), with v1's
 * choice per field when merging: add the new values, replace the target's, or skip them.
 * Identical fields have nothing to choose; they fold away.
 */
export default function CompareFields({
  fields,
  choices,
  onChoose,
  termName,
  targetName,
  labels,
}: {
  fields: readonly FieldComparison[];
  /** `null`: compare only, no choices (keep new, drop new). */
  choices: Readonly<Record<string, FieldAction>> | null;
  onChoose: (predicate: string, action: FieldAction) => void;
  termName: string;
  targetName: string;
  labels: Readonly<Record<string, string>>;
}) {
  const { t } = useTranslation();
  const [showSame, setShowSame] = useState(false);
  // Fields where the new term brings something: the only ones with a choice to make.
  const differing = fields.filter((field) => field.differs);
  const same = fields.filter((field) => !differing.includes(field));
  const shown = showSame ? fields : differing;
  return (
    <div className="space-y-1">
      <table className="w-full table-fixed border-collapse text-sm">
        <caption className="sr-only">{t("reviews.curation.compare.caption", { term: termName, target: targetName })}</caption>
        <thead>
          <tr className="app-text-muted text-left text-xs">
            <th scope="col" className="w-28 py-1 pr-2 font-medium">{t("reviews.curation.compare.field")}</th>
            <th scope="col" className="py-1 pr-2 font-medium">{t("reviews.curation.compare.newTerm", { name: termName })}</th>
            <th scope="col" className="py-1 pr-2 font-medium">{t("reviews.curation.compare.target", { name: targetName })}</th>
            {choices !== null ? <th scope="col" className="w-44 py-1 font-medium">{t("reviews.curation.compare.choice")}</th> : null}
          </tr>
        </thead>
        <tbody>
          {shown.map((field) => {
            const key = predicateKey(field.predicate);
            return (
              <tr key={field.predicate} className="border-t align-top">
                <th scope="row" className="py-1 pr-2 text-left font-medium" title={field.predicate}>
                  {key !== null ? t(`reviews.curation.fields.${key}`) : iriLocalName(field.predicate)}
                </th>
                <td className={classNames("py-1 pr-2", field.differs && changeToneClass("added"), field.differs && "rounded")}>
                  <Values values={field.termValues} labels={labels} />
                </td>
                <td className="py-1 pr-2">
                  <Values values={field.targetValues} labels={labels} />
                </td>
                {choices !== null ? (
                  <td className="py-1">
                    {field.termValues.length === 0 ? (
                      <span className="app-text-muted text-xs">{t("reviews.curation.compare.nothingToAdd")}</span>
                    ) : (
                      <FieldGroup legend={key !== null ? t(`reviews.curation.fields.${key}`) : iriLocalName(field.predicate)} legendHidden className="flex flex-wrap gap-x-2 text-xs">
                        {FIELD_ACTIONS.map((action) => (
                          <OptionLabel key={action}>
                            <input
                              type="radio"
                              name={`field-${field.predicate}`}
                              checked={(choices[field.predicate] ?? "skip") === action}
                              onChange={() => {
                                onChoose(field.predicate, action);
                              }}
                            />
                            {t(`reviews.curation.actions.${action}`)}
                          </OptionLabel>
                        ))}
                      </FieldGroup>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {same.length > 0 ? (
        <AppButton
          variant="ghost"
          size="xs"
          aria-expanded={showSame}
          onClick={() => {
            setShowSame(!showSame);
          }}
        >
          {showSame ? t("reviews.curation.compare.hideSame") : t("reviews.curation.compare.showSame", { count: same.length })}
        </AppButton>
      ) : null}
    </div>
  );
}

function Values({ values, labels }: { values: readonly FieldValue[]; labels: Readonly<Record<string, string>> }) {
  if (values.length === 0) {
    return <span className="app-text-muted">—</span>;
  }
  return (
    <ul className="space-y-0.5">
      {values.map((value) => (
        <li key={`${value.text}@${value.language ?? ""}`} className="line-clamp-3 break-words" title={value.text}>
          {value.isIri ? (labels[value.text] ?? iriLocalName(value.text)) : `“${value.text}”`}
          {value.language !== null ? <span className="app-text-muted text-xs"> {value.language}</span> : null}
        </li>
      ))}
    </ul>
  );
}
